// CardExtras Component - Handles free text, tags, and URL links for any card
import { useState, useEffect } from 'react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { X, Plus, Link as LinkIcon, Loader2, ExternalLink, ChevronDown, ChevronUp, Tag } from 'lucide-react';
import { cssVars, TAG_SUGGESTIONS, getTagColor } from './editorConstants';
import { unfurlLink } from '../../services/api';

// Tags Input Component
export function TagsInput({ tags = [], onChange, suggestions = [] }) {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const addTag = (tag) => {
    const trimmed = tag.trim();
    if (trimmed && !tags.includes(trimmed)) {
      onChange([...tags, trimmed]);
    }
    setInputValue('');
    setShowSuggestions(false);
  };

  const removeTag = (index) => {
    onChange(tags.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      removeTag(tags.length - 1);
    }
  };

  const filteredSuggestions = suggestions.filter(
    s => s.toLowerCase().includes(inputValue.toLowerCase()) && !tags.includes(s)
  );

  return (
    <div className="space-y-2">
      {/* Tags display */}
      <div className="flex flex-wrap gap-2">
        {tags.map((tag, i) => {
          const color = getTagColor(tag);
          return (
            <span
              key={i}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all hover:scale-105"
              style={{ backgroundColor: color.bg, color: color.text }}
            >
              {tag}
              <button
                onClick={() => removeTag(i)}
                className="ml-0.5 hover:opacity-70 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          );
        })}
      </div>

      {/* Input with suggestions */}
      <div className="relative">
        <div className="flex items-center gap-2">
          <Tag className="w-4 h-4 text-gray-400" />
          <Input
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setShowSuggestions(true);
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            placeholder="Add tags (press Enter or comma)"
            className="flex-1"
          />
        </div>
        
        {/* Suggestions dropdown */}
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
            {filteredSuggestions.slice(0, 8).map((suggestion, i) => (
              <button
                key={i}
                onClick={() => addTag(suggestion)}
                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: getTagColor(suggestion).bg }}
                />
                {suggestion}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Quick add suggestions */}
      {suggestions.length > 0 && tags.length < 5 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {suggestions.filter(s => !tags.includes(s)).slice(0, 5).map((s, i) => (
            <button
              key={i}
              onClick={() => addTag(s)}
              className="px-2 py-0.5 text-xs rounded-full border border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700 transition-colors"
            >
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// URL Link with Preview Component
export function LinkWithPreview({ link, onChange, onDelete }) {
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleUrlChange = async (url) => {
    onChange({ ...link, url });
    
    // Auto-unfurl when URL looks complete
    if (url && (url.startsWith('http://') || url.startsWith('https://')) && url.includes('.')) {
      setLoading(true);
      try {
        const data = await unfurlLink(url);
        if (data.title || data.image) {
          onChange({
            ...link,
            url,
            title: data.title || link.title,
            description: data.description || link.description,
            image: data.image || link.image,
          });
        }
      } catch (err) {
        // Silently fail - user can still enter manually
      } finally {
        setLoading(false);
      }
    }
  };

  const hasPreview = link.title || link.description || link.image;

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
      {/* URL Input */}
      <div className="p-3 flex items-center gap-2 border-b border-gray-100">
        <LinkIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <Input
          value={link.url || ''}
          onChange={(e) => handleUrlChange(e.target.value)}
          placeholder="https://example.com/article"
          className="flex-1 border-0 shadow-none focus-visible:ring-0 px-0"
        />
        {loading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
        <button
          onClick={onDelete}
          className="p-1 hover:bg-gray-100 rounded transition-colors"
        >
          <X className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      {/* Preview */}
      {hasPreview && (
        <div 
          className="p-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-start gap-3">
            {link.image && (
              <img
                src={link.image}
                alt=""
                className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                onError={(e) => e.target.style.display = 'none'}
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm text-gray-900 truncate">
                  {link.title || 'Untitled'}
                </span>
                <ExternalLink className="w-3 h-3 text-gray-400 flex-shrink-0" />
              </div>
              {link.description && (
                <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                  {link.description}
                </p>
              )}
            </div>
            {expanded ? (
              <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
            )}
          </div>
        </div>
      )}

      {/* Expanded edit mode */}
      {expanded && (
        <div className="p-3 space-y-3 border-t border-gray-100">
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Title</label>
            <Input
              value={link.title || ''}
              onChange={(e) => onChange({ ...link, title: e.target.value })}
              placeholder="Link title"
              className="text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Description</label>
            <Textarea
              value={link.description || ''}
              onChange={(e) => onChange({ ...link, description: e.target.value })}
              placeholder="Brief description"
              rows={2}
              className="text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Image URL</label>
            <Input
              value={link.image || ''}
              onChange={(e) => onChange({ ...link, image: e.target.value })}
              placeholder="https://example.com/image.jpg"
              className="text-sm"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Main Card Extras Editor Component
export default function CardExtras({ extras = {}, onChange, cardType }) {
  const [showExtras, setShowExtras] = useState(
    !!(extras.freeText || extras.tags?.length || extras.links?.length)
  );

  // Determine which tag suggestions to show based on card type
  const getTagSuggestions = () => {
    switch (cardType) {
      case 'HOW_I_INVEST':
      case 'INVESTMENT_FOCUS':
      case 'INVESTMENT_THESIS':
        return TAG_SUGGESTIONS.investment;
      case 'OUTSIDE_OF_WORK':
        return TAG_SUGGESTIONS.interests;
      case 'JOURNEY_TIMELINE':
      case 'HIGHLIGHTS':
        return TAG_SUGGESTIONS.skills;
      default:
        return TAG_SUGGESTIONS.general;
    }
  };

  const updateExtras = (key, value) => {
    onChange({ ...extras, [key]: value });
  };

  if (!showExtras) {
    return (
      <button
        onClick={() => setShowExtras(true)}
        className="w-full mt-4 py-2.5 px-4 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-500 hover:border-gray-300 hover:text-gray-700 transition-all flex items-center justify-center gap-2"
      >
        <Plus className="w-4 h-4" />
        Add extras (text, tags, links)
      </button>
    );
  }

  return (
    <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">Card Extras</span>
        <button
          onClick={() => setShowExtras(false)}
          className="text-xs text-gray-400 hover:text-gray-600"
        >
          Hide
        </button>
      </div>

      {/* Free Text */}
      <div>
        <label className="text-xs font-medium text-gray-500 mb-1.5 block">
          Additional Note
        </label>
        <Textarea
          value={extras.freeText || ''}
          onChange={(e) => updateExtras('freeText', e.target.value)}
          placeholder="Add any additional context or notes..."
          rows={2}
          className="text-sm resize-none"
        />
      </div>

      {/* Tags */}
      <div>
        <label className="text-xs font-medium text-gray-500 mb-1.5 block">
          Tags
        </label>
        <TagsInput
          tags={extras.tags || []}
          onChange={(tags) => updateExtras('tags', tags)}
          suggestions={getTagSuggestions()}
        />
      </div>

      {/* Links */}
      <div>
        <label className="text-xs font-medium text-gray-500 mb-1.5 block">
          Related Links
        </label>
        <div className="space-y-2">
          {(extras.links || []).map((link, i) => (
            <LinkWithPreview
              key={i}
              link={link}
              onChange={(updated) => {
                const links = [...(extras.links || [])];
                links[i] = updated;
                updateExtras('links', links);
              }}
              onDelete={() => {
                const links = (extras.links || []).filter((_, idx) => idx !== i);
                updateExtras('links', links);
              }}
            />
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => updateExtras('links', [...(extras.links || []), { url: '', title: '', description: '', image: '' }])}
            className="w-full"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Link
          </Button>
        </div>
      </div>
    </div>
  );
}

// Display component for showing extras in preview/public view
export function CardExtrasDisplay({ extras, compact = false }) {
  if (!extras || (!extras.freeText && !extras.tags?.length && !extras.links?.length)) {
    return null;
  }

  return (
    <div className={`space-y-3 ${compact ? 'mt-3' : 'mt-4 pt-4 border-t'}`} style={{ borderColor: cssVars.border }}>
      {/* Free text */}
      {extras.freeText && (
        <p className="text-sm" style={{ color: cssVars.textSecondary, lineHeight: 1.6 }}>
          {extras.freeText}
        </p>
      )}

      {/* Tags */}
      {extras.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {extras.tags.map((tag, i) => {
            const color = getTagColor(tag);
            return (
              <span
                key={i}
                className="px-2 py-0.5 rounded-full text-xs font-medium"
                style={{ backgroundColor: color.bg, color: color.text }}
              >
                {tag}
              </span>
            );
          })}
        </div>
      )}

      {/* Links */}
      {extras.links?.length > 0 && (
        <div className="space-y-2">
          {extras.links.filter(l => l.url).map((link, i) => (
            <a
              key={i}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-2.5 rounded-xl border transition-all hover:shadow-sm hover:border-gray-300"
              style={{ 
                borderColor: cssVars.border, 
                backgroundColor: cssVars.bg 
              }}
            >
              {link.image && (
                <img
                  src={link.image}
                  alt=""
                  className="w-12 h-12 object-cover rounded-lg flex-shrink-0"
                  onError={(e) => e.target.style.display = 'none'}
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="font-medium text-sm truncate" style={{ color: cssVars.text }}>
                    {link.title || link.url}
                  </span>
                  <ExternalLink className="w-3 h-3 flex-shrink-0" style={{ color: cssVars.textMuted }} />
                </div>
                {link.description && (
                  <p className="text-xs mt-0.5 line-clamp-1" style={{ color: cssVars.textMuted }}>
                    {link.description}
                  </p>
                )}
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
